import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { LayersIcon } from "../Components/Icons";
import { colorForFolderId } from "../lib/folders/folderColor.js";
import "./AssetsTable.css";

const STATUS_LABEL = { draft: "Draft", approved: "Approved", exported: "Exported" };

/**
 * Shared between the global Assets page and a brand's own Assets tab -
 * both are single-brand in this app (the global page forces a brand
 * selection above this table, rather than showing several brands mixed
 * together the way source's version did), so there's no brand column or
 * filter here at all, unlike source's fixedBrand-gated version.
 *
 * Folder handling has two modes, chosen by whether onMoveToFolder is
 * passed: with it, each row gets a "Move to..." dropdown (the global
 * page, where folder management lives); without it, each row just shows
 * a read-only tag naming its folder (the brand Assets tab - view only,
 * no management UI there).
 *
 * activeFolder ("all" | "unfiled" | a real folder id) is now owned by
 * FolderRail, the caller's own folder-selection UI - this component no
 * longer has its own internal folder filter/dropdown, since that would
 * be a second, redundant way to do the same thing. Status and date
 * filters stay internal, since they're orthogonal to folder selection.
 *
 * Each row gets a colored left-edge stripe matching its folder's color
 * (via colorForFolderId) - the client-derived, no-backend-change color
 * scheme folders now carry throughout the Assets UI.
 *
 * Delete is likewise optional - pass onDelete(asset) to show a per-row
 * delete button; confirmation and the actual API call belong to the
 * caller, this component just surfaces the click. deletingId disables
 * just the row currently being deleted, not the whole table.
 */
export default function AssetsTable({
  assets,
  brandSlug,
  folders,
  activeFolder = "all",
  onMoveToFolder,
  onDelete,
  deletingId,
  editable = true,
}) {
  const [statusFilter, setStatusFilter] = useState("all");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const visible = useMemo(
    () =>
      (assets ?? []).filter((asset) => {
        if (statusFilter !== "all" && asset.status !== statusFilter) return false;
        if (activeFolder === "unfiled" && asset.folderId) return false;
        if (activeFolder !== "all" && activeFolder !== "unfiled" && asset.folderId !== activeFolder) return false;
        const created = asset.createdAt ? asset.createdAt.slice(0, 10) : "";
        if (fromDate && created < fromDate) return false;
        if (toDate && created > toDate) return false;
        return true;
      }),
    [assets, statusFilter, activeFolder, fromDate, toDate]
  );

  const folderName = (folderId) => (folders ?? []).find((f) => f.id === folderId)?.name ?? null;

  return (
    <div className="assets-table">
      <div className="assets-table__filters">
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          aria-label="Filter by status"
          className="assets-table__filter-input"
        >
          <option value="all">All statuses</option>
          <option value="draft">Draft</option>
          <option value="approved">Approved</option>
          <option value="exported">Exported</option>
        </select>
        <input
          type="date"
          value={fromDate}
          onChange={(e) => setFromDate(e.target.value)}
          aria-label="Created from"
          className="assets-table__filter-input"
        />
        <span className="assets-table__filter-sep">to</span>
        <input
          type="date"
          value={toDate}
          onChange={(e) => setToDate(e.target.value)}
          aria-label="Created until"
          className="assets-table__filter-input"
        />
      </div>

      {visible.length === 0 ? (
        <div className="assets-table__empty">
          <span className="assets-table__empty-icon">
            <LayersIcon className="assets-table__empty-icon-svg" />
          </span>
          <h2 className="assets-table__empty-title">
            {(assets ?? []).length === 0 ? "No banners yet" : "Nothing matches these filters"}
          </h2>
          <p className="assets-table__empty-desc">
            {(assets ?? []).length === 0
              ? "Create your first banner set from the Generate wizard."
              : "Loosen a filter to see more."}
          </p>
        </div>
      ) : (
        <div className="assets-table__scroll">
          <table className="assets-table__table">
            <thead>
              <tr>
                <th>Code</th>
                <th>Sizes</th>
                <th>Status</th>
                <th>Folder</th>
                <th>Created</th>
                {onDelete ? <th>Actions</th> : null}
              </tr>
            </thead>
            <tbody>
              {visible.map((asset) => {
                const sizeCount = asset.recipe?.displaySizes?.length ?? 0;
                const rowColor = colorForFolderId(asset.folderId);
                return (
                  <tr key={asset.id} style={rowColor ? { "--row-stripe-color": rowColor.color } : undefined}>
                    <td>
                      <Link to={`/compose/${asset.id}?brand=${brandSlug}`} className="assets-table__code-link">
                        {asset.publicAssetCode ?? asset.id}
                      </Link>
                    </td>
                    <td className="assets-table__sizes-cell">
                      {sizeCount ? (
                        <span className="assets-table__size-badge">
                          {sizeCount} size{sizeCount === 1 ? "" : "s"}
                        </span>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td>
                      <span className={`assets-table__status-badge assets-table__status-badge--${asset.status}`}>
                        {STATUS_LABEL[asset.status] ?? asset.status}
                      </span>
                    </td>
                    <td>
                      {onMoveToFolder ? (
                        <select
                          value={asset.folderId ?? ""}
                          onChange={(e) => onMoveToFolder(asset.id, e.target.value || null)}
                          disabled={!editable}
                          aria-label={`Move ${asset.publicAssetCode ?? asset.id} to folder`}
                          className="assets-table__folder-select"
                        >
                          <option value="">Unfiled</option>
                          {(folders ?? []).map((f) => (
                            <option key={f.id} value={f.id}>
                              {f.name}
                            </option>
                          ))}
                        </select>
                      ) : asset.folderId && folderName(asset.folderId) ? (
                        <span className="assets-table__folder-tag">
                          <span className="assets-table__folder-dot" style={{ "--dot-color": rowColor?.color }} />
                          {folderName(asset.folderId)}
                        </span>
                      ) : (
                        <span className="assets-table__folder-tag assets-table__folder-tag--unfiled">Unfiled</span>
                      )}
                    </td>
                    <td className="assets-table__created-cell">
                      {asset.createdAt ? asset.createdAt.slice(0, 10) : "—"}
                    </td>
                    {onDelete ? (
                      <td>
                        <button
                          type="button"
                          disabled={!editable || deletingId === asset.id}
                          onClick={() => onDelete(asset)}
                          aria-label={`Delete ${asset.publicAssetCode ?? asset.id}`}
                          className="assets-table__delete-btn"
                        >
                          {deletingId === asset.id ? "Deleting…" : "Delete"}
                        </button>
                      </td>
                    ) : null}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}